import { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ButtonBase } from '../../../shared/Button';
import { useGenerateReports } from './useGenerateReports';
import { useCampaignFilter } from '../hooks/useCampaignFilter';
import { ActionCableContext } from '../../../lib/actionCableContext';
import { useFetchReportsStatus } from './useFetchReportsStatus';
import { useFetchCampaigns } from '../../campaigns/hooks/useFetchCampaigns';

const DownloadReports = () => {
  const cable = useContext(ActionCableContext);
  const { t } = useTranslation('reports');

  const { campaigns } = useFetchCampaigns();
  const { data } = useFetchReportsStatus();
  const [generating, setGenerating] = useState([
    { status: 'not-started', campaignId: null },
  ]);
  const { campaignId } = useCampaignFilter();
  const currentReport =
    generating.find(report => report.campaignId === campaignId) || {};

  const { generateReport } = useGenerateReports(campaignId);

  useEffect(() => {
    if (campaigns) {
      setGenerating(prevState => [
        ...prevState,
        ...campaigns.map(campaign => ({
          status: 'not-started',
          campaignId: campaign.uuid,
        })),
      ]);
    }
  }, [campaigns]);

  useEffect(() => {
    const isCampaignReportProcessing = data.find(
      ({ report_request: reportRequest }) =>
        reportRequest.params.campaign_id === campaignId,
    );
    const isBoardReportProcessing =
      data.find(
        ({ report_request: reportRequest }) => !!reportRequest.params.demo_id,
      ) && campaignId === null;

    if (isCampaignReportProcessing || isBoardReportProcessing) {
      setGenerating(prevState =>
        prevState.map(report => {
          if (report.campaignId === campaignId) {
            return {
              campaignId,
              status: 'processing',
            };
          }
          return report;
        }),
      );
    }
  }, [campaignId, data]);

  useEffect(() => {
    if (!generating.find(report => report.campaignId === campaignId)) {
      setGenerating(prevState => [
        ...prevState,
        { status: 'not-started', campaignId },
      ]);
    }
  }, [campaignId, generating]);

  useEffect(() => {
    const channel = cable.subscriptions.create(
      {
        channel: 'DownloadReportChannel',
      },
      {
        received: reportData => {
          const reportCampaignId = reportData.campaign_uuid || null;

          if (reportData.status === 'running') {
            setGenerating(prevState =>
              prevState.map(report => {
                if (report.campaignId === reportCampaignId) {
                  return {
                    campaignId: reportCampaignId,
                    status: 'running',
                  };
                }
                return report;
              }),
            );
          }
          if (reportData.status === 'finished') {
            setGenerating(prevState =>
              prevState.map(report => {
                if (report.campaignId === reportCampaignId) {
                  return {
                    campaignId: reportCampaignId,
                    status: 'finished',
                    url: reportData.url,
                  };
                }
                return report;
              }),
            );
          }
        },
      },
    );

    return () => {
      channel.unsubscribe();
    };
  }, [cable.subscriptions]);

  return (
    <>
      {currentReport.status === 'not-started' && (
        <DownloadButton
          onClick={() => {
            generateReport();
          }}
        >
          <i className="fa-regular fa-lg fa-file-lines"></i>
          {t('downloadReports.notStarted')}
        </DownloadButton>
      )}
      {currentReport.status === 'processing' && (
        <DownloadButton disabled={true}>
          <i className="fa-solid fa-lg fa-hourglass"></i>
          {t('downloadReports.processing')}
        </DownloadButton>
      )}
      {currentReport.status === 'running' && (
        <DownloadButton>{t('downloadReports.generating')}</DownloadButton>
      )}
      {currentReport.status === 'finished' && (
        <DownloadButton as="a" target="_blank" href={currentReport.url}>
          <i className="fa-solid fa-lg fa-download"></i>
          {t('downloadReports.finished')}
        </DownloadButton>
      )}
    </>
  );
};

export default DownloadReports;

const DownloadButton = styled(ButtonBase).attrs({
  mode: 'contained',
  variant: 'primary',
})`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  height: initial;
`;
